import { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { SERVICES } from "@/lib/brand";
import { Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteDialog = ({ open, onOpenChange }) => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    project_type: "",
    location: "",
    message: "",
  });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "quote-dialog" });
      setSuccess(true);
      toast.success("Thank you! Our team will reach out within 24 hours.");
      setTimeout(() => {
        setSuccess(false);
        onOpenChange(false);
        setForm({
          name: "",
          email: "",
          phone: "",
          service: "",
          project_type: "",
          location: "",
          message: "",
        });
      }, 1800);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-dialog"
        className="sm:max-w-[600px] p-0 overflow-hidden rounded-2xl border-none"
      >
        <div className="bg-[#13294B] text-white px-8 py-7">
          <DialogHeader className="text-left space-y-2">
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">
              Request a Free Quote
            </DialogTitle>
            <DialogDescription className="text-white/70 text-[15px]">
              Tell us about your project. We respond within 24 hours.
            </DialogDescription>
          </DialogHeader>
        </div>

        {success ? (
          <div className="px-8 py-16 flex flex-col items-center text-center">
            <CheckCircle2 size={48} className="text-[#13294B]" />
            <h3 className="font-display text-xl font-bold mt-4">
              Request received
            </h3>
            <p className="text-[#707070] mt-2 text-sm">
              Our project consultant will reach out shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="px-8 py-7 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="q-name" className="text-xs uppercase tracking-wider text-[#707070]">
                  Full Name *
                </Label>
                <Input
                  id="q-name"
                  data-testid="quote-input-name"
                  value={form.name}
                  onChange={update("name")}
                  required
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-phone" className="text-xs uppercase tracking-wider text-[#707070]">
                  Phone *
                </Label>
                <Input
                  id="q-phone"
                  data-testid="quote-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  required
                  className="h-11"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="q-email" className="text-xs uppercase tracking-wider text-[#707070]">
                Email *
              </Label>
              <Input
                id="q-email"
                type="email"
                data-testid="quote-input-email"
                value={form.email}
                onChange={update("email")}
                required
                className="h-11"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#707070]">
                  Service
                </Label>
                <Select value={form.service} onValueChange={update("service")}>
                  <SelectTrigger data-testid="quote-select-service" className="h-11">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.slug} value={s.title}>
                        {s.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-location" className="text-xs uppercase tracking-wider text-[#707070]">
                  Location
                </Label>
                <Input
                  id="q-location"
                  data-testid="quote-input-location"
                  value={form.location}
                  onChange={update("location")}
                  placeholder="e.g. Downtown Dubai"
                  className="h-11"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="q-message" className="text-xs uppercase tracking-wider text-[#707070]">
                Project Details
              </Label>
              <Textarea
                id="q-message"
                data-testid="quote-input-message"
                rows={4}
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us about your project, timeline and budget."
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="quote-submit-btn"
              className="w-full bg-[#13294B] hover:bg-[#0E1F3A] text-white rounded-full py-4 font-medium text-[15px] transition flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? "Submitting…" : "Send Enquiry"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
